// Preview resize layer (REF-5696) — routes app_cdn images through the imgproxy
// resize+cache endpoint so display/grid/thumbnail slots download a small render
// instead of the full-res original. Contract from DevOps (Ahmet):
//
//   https://img.refabric.com/insecure/rs:fit:<w>/plain/<original-app_cdn-URL>
//
// Rules:
//   • Use ONLY for display (grid / card / thumbnail). Download, canvas placement,
//     and re-generation MUST keep the original URL — do not wrap those.
//   • Allowed widths are 128 / 256 / 512 only; any other value 403s at the edge,
//     so we snap to the nearest allowed width. For retina, ask for 2× the CSS box.
//   • Non-app_cdn URLs (trial `/watermark/...`, `data:`, external, bundled assets)
//     are returned untouched — the proxy only fronts the app_cdn bucket, and
//     resizing a watermarked preview would break it.
const RESIZE_HOST = "https://img.refabric.com";
const GCS_BASE = "https://storage.googleapis.com/app_cdn/";
const ALLOWED_WIDTHS = [128, 256, 512];

export function previewUrl(url?: string, { w = 256 }: { w?: number } = {}): string | undefined {
  if (!url) return url;

  // Support both full and relative app_cdn paths; leave everything else as-is.
  let full: string;
  if (url.startsWith(GCS_BASE)) full = url;
  else if (url.startsWith("app_cdn/")) full = "https://storage.googleapis.com/" + url;
  else return url; // watermark (/watermark/...), data:, external URL → don't touch

  // Snap to an allowed width (the edge only accepts 128 / 256 / 512).
  const width = ALLOWED_WIDTHS.includes(w) ? w : ALLOWED_WIDTHS.reduce((a, b) => (Math.abs(b - w) < Math.abs(a - w) ? b : a));

  return `${RESIZE_HOST}/insecure/rs:fit:${width}/plain/${full}`;
}

// The inverse of `previewUrl` (REF-6468): the original behind a resize-edge url, or the url
// untouched when it is not one. A display slot uses it to retry the ORIGINAL once when the edge
// cannot render the source — the edge refuses a source over IMGPROXY_MAX_SRC_RESOLUTION (50 MP)
// with a 422, and a 4K studio render can be 67 MP, so without the retry a completed project's
// card went grey. The trailing `@<format>` that `cardStillUrl` appends is dropped too.
const PROXY_PREFIX = `${RESIZE_HOST}/insecure/`;
const PLAIN_SEGMENT = "/plain/";
const FORMAT_SUFFIX = /@(jpg|jpeg|png|webp|avif)$/i;

export function originalOfPreview(url: string): string {
  if (!url.startsWith(PROXY_PREFIX)) return url;

  const idx = url.indexOf(PLAIN_SEGMENT, PROXY_PREFIX.length);

  return idx === -1 ? url : url.slice(idx + PLAIN_SEGMENT.length).replace(FORMAT_SUFFIX, "");
}

// What's New card still (REF-6739). Feature emails carry their demo as a 5–10 MB animated GIF, and
// the panel renders up to ten cards at once — so the card asks imgproxy for a 512px JPEG of the FIRST
// FRAME (`@jpg` on the plain source url) and the full GIF only plays in FeatureTourModal. JPEG, not
// WebP: an animated WebP is still animated, so with `@webp` "first frame only" would rest on the
// server's IMGPROXY_MAX_ANIMATION_FRAMES (default 1) — raise it and every card silently becomes a
// multi-MB animated WebP. JPEG cannot animate, so the url itself guarantees one frame (~20 KB). Demo
// GIFs need no transparency. Display-only, like previewUrl. A non-GIF, an ineligible host, or a source
// url carrying a query/hash (which would swallow the `@` suffix) comes back untouched.
export function cardStillUrl(url?: string): string | undefined {
  if (!url || /[?#]/.test(url) || !/\.gif$/i.test(url)) return url;

  const resized = previewUrl(url, { w: 512 });
  if (!resized || resized === url) return url;

  return `${resized}@jpg`;
}
